/**
 * Plain words for the graph editor's step panel and canvas (ADR-0058, "advanced is easy too").
 *
 * - `humaniseName`: a readable label for a technical name (`script_timeout_s` → "Script timeout
 *   (seconds)"); the UI prefers a translated label or the manifest's `title` when there is one.
 * - `describeRef`: where a `{{ state.x.y }}` value comes from (the step that writes `x`, and `y`).
 * - `argView`: how to show a tool argument: from an earlier step, a plain value, or code/data set
 *   by the developer (shown as a short summary, kept under "Advanced").
 * - `describeCondition`: an edge's `when` expression in a structured, translatable form.
 */
import type { JsonSchemaLite, Text } from "../types";
import { parseCondition, stateValues, templateKey, type ArgField, type ConditionOp, type StateValue } from "./plain";
import { fileRefs } from "./clear";
import type { AgentSpec, SpecNode } from "./types";

const ACRONYMS: Record<string, string> = { url: "URL", urls: "URLs", id: "ID", ids: "IDs", api: "API", csv: "CSV", json: "JSON", sla: "SLA", vat: "VAT", iban: "IBAN", pdf: "PDF", sql: "SQL", http: "HTTP", mb: "MB", kb: "KB", usd: "USD" };
const UNITS: Record<string, string> = { s: "seconds", sec: "seconds", ms: "milliseconds", mb: "MB", kb: "KB", usd: "USD", pct: "%" };

/** A technical name in readable words: `script_timeout_s` → "Script timeout (seconds)". */
export function humaniseName(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_\-.]+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase());
  if (!words.length) return name;
  let unit: string | null = null;
  const last = words[words.length - 1] as string;
  if (words.length > 1 && UNITS[last]) {
    unit = UNITS[last] as string;
    words.pop();
  }
  const text = words.map((w, i) => ACRONYMS[w] ?? (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
  return unit ? `${text} (${unit})` : text;
}

/**
 * Names for the steps the guided builder, the examples and the demo agents make without a name of
 * their own, in both languages (Builder sweep: Arabic screens showed "Think", "Good enough", ...).
 */
const GENERATED_NAMES: Record<string, Text> = {
  think: { en: "Think", ar: "التفكير" },
  answer: { en: "Answer", ar: "الإجابة" },
  classify: { en: "Sort the request", ar: "تصنيف الطلب" },
  route: { en: "Choose the way", ar: "اختيار الطريق" },
  fallback: { en: "Answer other requests", ar: "الرد على الطلبات الأخرى" },
  plan: { en: "Plan", ar: "التخطيط" },
  execute: { en: "Do the next part", ar: "تنفيذ الجزء التالي" },
  replan: { en: "More to do?", ar: "هل بقي عمل؟" },
  summarise: { en: "Summarise", ar: "التلخيص" },
  draft: { en: "Write a draft", ar: "كتابة مسودة" },
  critique: { en: "Check the draft", ar: "مراجعة المسودة" },
  good_enough: { en: "Good enough?", ar: "هل هي جيدة بما يكفي؟" },
  revise: { en: "Improve the draft", ar: "تحسين المسودة" },
  check: { en: "Check", ar: "الفحص" },
  report: { en: "Report", ar: "التقرير" },
  next_step: { en: "Suggest the next step", ar: "اقتراح الخطوة التالية" },
  review: { en: "A person reviews", ar: "مراجعة شخص" },
  act: { en: "Use a system", ar: "استخدام نظام" },
};

/** The bilingual name of a step made without one (`retrieve_2` → "Search knowledge 2"), or null. */
export function generatedStepName(id: string, lang: "ar" | "en"): string | null {
  const known = GENERATED_NAMES[id];
  if (known) return known[lang];
  const m = /^(retrieve|topic)_(\d+)$/.exec(id);
  if (m) return m[1] === "retrieve" ? (lang === "ar" ? `البحث في المعرفة ${m[2]}` : `Search knowledge ${m[2]}`) : lang === "ar" ? `الموضوع ${m[2]}` : `Topic ${m[2]}`;
  return null;
}

/** A step's display name: its label in `lang` (or the other language), else a known name, else its id made readable. */
export function stepLabel(node: Pick<SpecNode, "id" | "label"> | undefined, lang: "ar" | "en", fallbackId = ""): string {
  if (!node) return generatedStepName(fallbackId, lang) ?? humaniseName(fallbackId);
  const label = node.label ? node.label[lang] || node.label.en || node.label.ar : "";
  return label || generatedStepName(node.id, lang) || humaniseName(node.id);
}

/** Text in `lang` from a manifest value that may be a plain string or `{en, ar}`. */
export function pickTitle(v: unknown, lang: "ar" | "en"): string | null {
  if (typeof v === "string") return v.trim() || null;
  if (v && typeof v === "object") {
    const t = v as Partial<Text>;
    return t[lang] || t.en || t.ar || null;
  }
  return null;
}

// ---------------------------------------------------------------- values from earlier steps

export interface ValueRef {
  /** The full state path, e.g. `request.pages`. */
  path: string;
  /** The value of the step (or request) that writes the first part of the path; null if unknown. */
  value: StateValue | null;
  /** The rest of the path inside that value (`pages`), or null when the whole value is used. */
  field: string | null;
}

/** Where a state path comes from: the longest matching value an upstream step writes. */
export function describeRef(spec: AgentSpec, path: string): ValueRef {
  const values = stateValues(spec);
  const parts = path.split(".");
  for (let n = parts.length; n > 0; n--) {
    const key = parts.slice(0, n).join(".");
    const value = values.find((v) => v.key === key);
    if (value) return { path, value, field: n < parts.length ? parts.slice(n).join(".") : null };
  }
  return { path, value: null, field: null };
}

// ---------------------------------------------------------------- tool arguments

export type ArgKind = "empty" | "fromStep" | "text" | "number" | "boolean" | "code" | "data";

export interface ArgView {
  kind: ArgKind;
  /** For `fromStep`: the state path. */
  path?: string;
  /** For `code`: size in bytes. */
  size?: number;
  /** For `data`: number of values (object keys or list items). */
  count?: number;
  /** For `code`: the agent files it uses (`{"$file": name}`, ADR-0071). */
  files?: string[];
  /** Shown under "Advanced" (code, data and technical settings such as time limits). */
  advanced: boolean;
}

const LONG_TEXT = 160;
/** Names of settings that are technical by nature (scripts, time limits, raw options). */
const TECHNICAL = /(^|_)(script|code|files|source|timeout|retries|retry|memory|stdin|headers|options|args)(_|$)|_(s|ms|mb)$/;

const byteSize = (s: string) => new TextEncoder().encode(s).length;

/** How an argument is shown in the step panel. */
export function argView(name: string, value: unknown, field?: Pick<ArgField, "type" | "required">): ArgView {
  const technical = TECHNICAL.test(name.toLowerCase());
  if (value === undefined || value === null || value === "") return { kind: "empty", advanced: technical && !field?.required };
  const fromStep = templateKey(value);
  if (fromStep) return { kind: "fromStep", path: fromStep, advanced: technical };
  const files = fileRefs(value);
  if (files.length && value && typeof value === "object" && !Array.isArray(value) && ("$file" in (value as object) || !Object.values(value as object).some((v) => typeof v === "string"))) {
    return { kind: "code", size: 0, files, advanced: true };
  }
  if (value && typeof value === "object" && !Array.isArray(value) && "$literal" in (value as Record<string, unknown>)) {
    const lit = (value as { $literal: unknown }).$literal;
    return { kind: "code", size: byteSize(typeof lit === "string" ? lit : JSON.stringify(lit)), advanced: true };
  }
  if (typeof value === "string") {
    if (value.length > LONG_TEXT || value.includes("\n") || (technical && /[{}()=>;]/.test(value))) return { kind: "code", size: byteSize(value), advanced: true };
    return { kind: "text", advanced: technical };
  }
  if (typeof value === "number") return { kind: "number", advanced: technical };
  if (typeof value === "boolean") return { kind: "boolean", advanced: technical };
  const count = Array.isArray(value) ? value.length : Object.keys(value as object).length;
  // The Code runner's `input` holds the step's key values (its mode, its data): kept in view.
  return { kind: "data", count, size: byteSize(JSON.stringify(value)), advanced: name !== "input" };
}

/** The schema of one argument (for its title), if the action publishes one. */
export function argSchema(schema: JsonSchemaLite | undefined, name: string): JsonSchemaLite | undefined {
  // A dotted path names a field inside a group (`body.amount`); nullable fields look through.
  return name.split(".").reduce<JsonSchemaLite | undefined>((s, part) => {
    const props = s?.properties ?? (s?.anyOf?.find((x): x is JsonSchemaLite => x.type !== "null")?.properties);
    return props?.[part];
  }, schema);
}

// ---------------------------------------------------------------- conditions

export type PlainOp = ConditionOp | "ok" | "not_ok";

export interface PlainClause {
  ref: ValueRef;
  op: PlainOp;
  value?: string;
  /** "Within" (ADR-0103): the value compared with, and the tolerance. */
  other?: ValueRef;
  tolerance?: string;
}

export type PlainCondition =
  | { kind: "always" }
  /** ReAct routing: the AI asked for this system action. */
  | { kind: "toolCall"; tool: string; action: string }
  /** ReAct routing: the AI answered without asking a system. */
  | { kind: "noToolCall" }
  | { kind: "clauses"; clauses: PlainClause[] }
  | { kind: "raw"; raw: string };

const NEGATE: Record<ConditionOp, ConditionOp> = {
  filled: "empty",
  empty: "filled",
  equals: "not_equals",
  not_equals: "equals",
  not_is: "equals",
  more_than: "at_most",
  at_most: "more_than",
  less_than: "at_least",
  at_least: "less_than",
  approved: "rejected",
  rejected: "approved",
  within: "not_within",
  not_within: "within",
};
const OK = new Set(["ok", "success", "succeeded", "done", "true"]);

function clauseOf(spec: AgentSpec, part: string): PlainClause | null {
  let src = part.trim();
  let negated = false;
  // `not state.x == 'ok'` is `not (state.x == 'ok')` in the spec expression language.
  const m = /^not\s+(?!exists\()(.*)$/.exec(src);
  if (m) {
    negated = true;
    src = (m[1] as string).replace(/^\((.*)\)$/, "$1");
  }
  const parsed = parseCondition(src);
  if (!parsed || parsed.length !== 1) return null;
  const c = parsed[0] as NonNullable<(typeof parsed)[0]>;
  const op = negated ? NEGATE[c.op] : c.op;
  const ref = describeRef(spec, c.key);
  const last = c.key.split(".").pop() ?? "";
  if (last === "error" && (op === "filled" || op === "empty") && c.key.includes(".")) {
    // `exists(state.x.error)`: the system step x did not work (ADR-0079); missing: it did.
    const parent = c.key.split(".").slice(0, -1).join(".");
    return { ref: describeRef(spec, parent), op: op === "filled" ? "not_ok" : "ok" };
  }
  if (last === "status" && (op === "equals" || op === "not_equals") && OK.has((c.value ?? "").toLowerCase())) {
    // "status is ok" reads as "worked": the subject is the step, not its status field.
    const parent = c.key.split(".").slice(0, -1).join(".");
    return { ref: parent ? describeRef(spec, parent) : ref, op: op === "equals" ? "ok" : "not_ok" };
  }
  return { ref, op, ...(c.value !== undefined ? { value: c.value } : {}), ...(c.other ? { other: describeRef(spec, c.other) } : {}), ...(c.tolerance !== undefined ? { tolerance: c.tolerance } : {}) };
}

/** An edge condition in a structured form the UI can put in plain words; `raw` when it can't. */
export function describeCondition(spec: AgentSpec, expr: string | null | undefined): PlainCondition {
  const src = (expr ?? "").trim();
  if (!src) return { kind: "always" };
  if (/^not exists\(state\.tool_call\)$/.test(src)) return { kind: "noToolCall" };
  const call = /^exists\(state\.tool_call\) and state\.tool_call\.tool == ['"]([^'"]+)['"] and state\.tool_call\.action == ['"]([^'"]+)['"]$/.exec(src);
  if (call) return { kind: "toolCall", tool: call[1] as string, action: call[2] as string };
  if (/\bor\b/.test(src)) return { kind: "raw", raw: src };
  const clauses: PlainClause[] = [];
  for (const part of src.split(/\s+and\s+/)) {
    const c = clauseOf(spec, part);
    if (!c) return { kind: "raw", raw: src };
    clauses.push(c);
  }
  return { kind: "clauses", clauses };
}
